/**
 * Patrol Plans Management Page
 * 
 * Admin page for creating and managing patrol plans (grouped site runs).
 * Supports: list, create, edit, soft delete
 * 
 * FIX: Extracted PlanForm as stable component to prevent re-mounting on keystroke
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../lib/auth';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import {
  Plus,
  Pencil,
  Trash2,
  Search,
  MapPin,
  Calendar,
  Clock,
  Building2,
  Route,
  ChevronUp,
  ChevronDown,
  GripVertical,
  X,
  CheckCircle,
  AlertCircle,
  Loader2
} from 'lucide-react';

const DAYS_OF_WEEK = [
  { value: 1, label: 'Mon' },
  { value: 2, label: 'Tue' },
  { value: 3, label: 'Wed' },
  { value: 4, label: 'Thu' },
  { value: 5, label: 'Fri' },
  { value: 6, label: 'Sat' },
  { value: 7, label: 'Sun' },
];

const REGIONS = ['NSW', 'VIC', 'QLD', 'SA', 'WA', 'TAS', 'NT', 'ACT'];

// =============================================================================
// PLAN FORM COMPONENT - Extracted to prevent re-mounting
// =============================================================================
function PlanForm({ 
  formData, 
  setFormData, 
  sites, 
  clients, 
  onSubmit, 
  submitLabel, 
  formLoading 
}) {
  // Toggle day selection
  const toggleDay = (day) => {
    const currentDays = formData.schedule?.days_of_week || [];
    const newDays = currentDays.includes(day)
      ? currentDays.filter(d => d !== day)
      : [...currentDays, day].sort((a, b) => a - b);
    
    setFormData(prev => ({
      ...prev,
      schedule: { ...prev.schedule, days_of_week: newDays }
    }));
  };

  // Add site to plan
  const addSite = (siteId) => {
    if (!siteId) return;
    const site = sites.find(s => s.id === siteId);
    if (!site) return;
    
    // Check if already added
    if (formData.sites.some(s => s.site_id === siteId)) {
      toast.error('Site already in plan');
      return;
    }
    
    setFormData(prev => ({
      ...prev,
      sites: [
        ...prev.sites,
        {
          site_id: siteId,
          site_name: site.name,
          order: prev.sites.length,
          expected_duration_minutes: 30,
          instructions: ''
        }
      ]
    }));
  };

  // Remove site from plan
  const removeSite = (siteId) => {
    setFormData(prev => ({
      ...prev,
      sites: prev.sites.filter(s => s.site_id !== siteId).map((s, i) => ({ ...s, order: i }))
    }));
  };

  // Move site up/down
  const moveSite = (index, direction) => {
    const newSites = [...formData.sites];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    
    if (targetIndex < 0 || targetIndex >= newSites.length) return;
    
    [newSites[index], newSites[targetIndex]] = [newSites[targetIndex], newSites[index]];
    newSites.forEach((s, i) => s.order = i);
    
    setFormData(prev => ({ ...prev, sites: newSites }));
  };

  // Handle name change
  const handleNameChange = (e) => {
    const value = e.target.value;
    setFormData(prev => ({ ...prev, name: value }));
  };

  // Handle description change
  const handleDescriptionChange = (e) => {
    const value = e.target.value;
    setFormData(prev => ({ ...prev, description: value }));
  };

  return (
    <div className="space-y-6 max-h-[70vh] overflow-y-auto pr-2">
      {/* Basic Info */}
      <div className="grid grid-cols-2 gap-4">
        <div className="col-span-2">
          <Label htmlFor="plan-name">Plan Name *</Label>
          <Input
            id="plan-name"
            name="plan-name"
            autoComplete="off"
            value={formData.name}
            onChange={handleNameChange}
            placeholder="e.g., Newcastle Morning Run"
            className="mt-1"
            data-testid="plan-name-input"
          />
        </div>
        
        <div>
          <Label htmlFor="region">Region</Label>
          <Select
            value={formData.region || 'none'}
            onValueChange={(v) => setFormData(prev => ({ ...prev, region: v === 'none' ? '' : v }))}
          >
            <SelectTrigger className="mt-1">
              <SelectValue placeholder="Select region" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">No region</SelectItem>
              {REGIONS.map(r => (
                <SelectItem key={r} value={r}>{r}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        
        <div>
          <Label htmlFor="client">Client ({clients.length} available)</Label>
          <Select
            value={formData.client_id || 'none'}
            onValueChange={(v) => setFormData(prev => ({ ...prev, client_id: v === 'none' ? '' : v }))}
          >
            <SelectTrigger className="mt-1">
              <SelectValue placeholder="Select client" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">No client</SelectItem>
              {clients.map(c => (
                <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        
        <div className="col-span-2">
          <Label htmlFor="description">Description</Label>
          <Textarea
            id="description"
            name="description"
            value={formData.description}
            onChange={handleDescriptionChange}
            placeholder="Optional description..."
            className="mt-1"
            rows={2}
          />
        </div>
      </div>
      
      {/* Schedule */}
      <div className="space-y-3 border-t border-zinc-700 pt-4">
        <h4 className="font-medium text-zinc-300 flex items-center gap-2">
          <Calendar className="w-4 h-4" /> Schedule
        </h4>
        
        <div>
          <Label className="text-xs text-zinc-500">Days of Week</Label>
          <div className="flex gap-1 mt-1">
            {DAYS_OF_WEEK.map(day => (
              <Button
                key={day.value}
                type="button"
                size="sm"
                variant={formData.schedule?.days_of_week?.includes(day.value) ? 'default' : 'outline'}
                className={`px-3 ${formData.schedule?.days_of_week?.includes(day.value) ? 'bg-blue-600' : ''}`}
                onClick={() => toggleDay(day.value)}
              >
                {day.label}
              </Button>
            ))}
          </div>
        </div>
        
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label htmlFor="start_time">Start Time</Label>
            <Input
              id="start_time"
              type="time"
              value={formData.schedule?.start_time || '06:00'}
              onChange={(e) => setFormData(prev => ({
                ...prev,
                schedule: { ...prev.schedule, start_time: e.target.value }
              }))}
              className="mt-1"
            />
          </div>
          <div>
            <Label htmlFor="end_time">End Time</Label>
            <Input
              id="end_time"
              type="time"
              value={formData.schedule?.end_time || '14:00'}
              onChange={(e) => setFormData(prev => ({
                ...prev,
                schedule: { ...prev.schedule, end_time: e.target.value }
              }))}
              className="mt-1"
            />
          </div>
        </div>
      </div>
      
      {/* Patrol Config */}
      <div className="space-y-3 border-t border-zinc-700 pt-4">
        <h4 className="font-medium text-zinc-300 flex items-center gap-2">
          <Clock className="w-4 h-4" /> Patrol Requirements
        </h4>
        
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label htmlFor="expected_visits">Expected Site Visits</Label>
            <Input
              id="expected_visits"
              type="number"
              min="1"
              value={formData.patrol_config?.expected_visits || 1}
              onChange={(e) => setFormData(prev => ({
                ...prev,
                patrol_config: { ...prev.patrol_config, expected_visits: parseInt(e.target.value) || 1 }
              }))}
              className="mt-1"
            />
          </div>
          <div>
            <Label htmlFor="expected_duration">Expected Duration (mins)</Label>
            <Input
              id="expected_duration"
              type="number"
              min="15"
              step="15"
              value={formData.patrol_config?.expected_duration_minutes || 480}
              onChange={(e) => setFormData(prev => ({
                ...prev,
                patrol_config: { ...prev.patrol_config, expected_duration_minutes: parseInt(e.target.value) || 480 }
              }))}
              className="mt-1"
            />
          </div>
        </div>
      </div>
      
      {/* Sites */}
      <div className="space-y-3 border-t border-zinc-700 pt-4">
        <h4 className="font-medium text-zinc-300 flex items-center gap-2">
          <MapPin className="w-4 h-4" /> Sites ({formData.sites?.length || 0}) - {sites.length} available
        </h4>
        
        <div>
          <Label className="text-xs text-zinc-500">Add Site</Label>
          <Select 
            onValueChange={(val) => val && addSite(val)} 
            value={undefined}
          >
            <SelectTrigger className="mt-1">
              <SelectValue placeholder="Select a site to add..." />
            </SelectTrigger>
            <SelectContent className="max-h-60">
              {sites.filter(s => !formData.sites?.some(fs => fs.site_id === s.id)).map(site => (
                <SelectItem key={site.id} value={site.id}>
                  <span className="flex items-center gap-2">
                    <MapPin className="w-3 h-3 text-zinc-500" />
                    {site.name}
                    {site.region && <Badge variant="outline" className="text-xs ml-2">{site.region}</Badge>}
                  </span>
                </SelectItem>
              ))}
              {sites.length === 0 && (
                <div className="py-2 px-4 text-sm text-zinc-500">No sites available</div>
              )}
            </SelectContent>
          </Select>
        </div>
        
        {/* Site List */}
        {formData.sites?.length > 0 && (
          <div className="space-y-2 mt-3">
            {formData.sites.map((site, index) => (
              <div
                key={site.site_id}
                className="flex items-center gap-2 bg-zinc-800/50 rounded-lg p-2 border border-zinc-700"
              >
                <GripVertical className="w-4 h-4 text-zinc-600" />
                <span className="text-sm text-zinc-400 w-6">{index + 1}.</span>
                <span className="flex-1 text-sm text-zinc-200 truncate">
                  {site.site_name || sites.find(s => s.id === site.site_id)?.name || site.site_id}
                </span>
                <div className="flex gap-1">
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-7 w-7 p-0"
                    onClick={() => moveSite(index, 'up')}
                    disabled={index === 0}
                  >
                    <ChevronUp className="w-4 h-4" />
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-7 w-7 p-0"
                    onClick={() => moveSite(index, 'down')}
                    disabled={index === formData.sites.length - 1}
                  >
                    <ChevronDown className="w-4 h-4" />
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-7 w-7 p-0 text-red-400 hover:text-red-300"
                    onClick={() => removeSite(site.site_id)}
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      
      <DialogFooter className="border-t border-zinc-700 pt-4">
        <Button
          onClick={onSubmit}
          disabled={formLoading || !formData.name?.trim()}
          className="bg-blue-600 hover:bg-blue-700"
          data-testid="submit-patrol-plan-btn"
        >
          {formLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
          {submitLabel}
        </Button>
      </DialogFooter>
    </div>
  );
}

// =============================================================================
// MAIN PAGE COMPONENT
// =============================================================================
export default function PatrolPlansPage() {
  const { api } = useAuth();
  const [plans, setPlans] = useState([]);
  const [sites, setSites] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterRegion, setFilterRegion] = useState('all');
  const [filterActive, setFilterActive] = useState('active');
  
  // Dialog states
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    region: '',
    client_id: '',
    description: '',
    schedule: {
      days_of_week: [1, 2, 3, 4, 5],
      start_time: '06:00',
      end_time: '14:00',
      timezone: 'Australia/Sydney'
    },
    patrol_config: {
      expected_visits: 1,
      expected_duration_minutes: 480,
      allow_out_of_order: true,
      require_site_checkout: true
    },
    sites: []
  });
  const [formLoading, setFormLoading] = useState(false);

  // Fetch patrol plans
  const fetchPlans = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (filterActive === 'active') params.append('is_active', 'true');
      if (filterActive === 'inactive') params.append('is_active', 'false');
      if (filterRegion !== 'all') params.append('region', filterRegion);
      if (searchQuery) params.append('search', searchQuery);
      
      const res = await api.get(`/patrol-plans?${params}`);
      setPlans(res.data.items || []);
    } catch (err) {
      console.error('Failed to fetch patrol plans:', err);
      toast.error('Failed to load patrol plans');
    } finally {
      setLoading(false);
    }
  }, [api, filterActive, filterRegion, searchQuery]);

  // Fetch sites and clients - only once on mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch sites
        console.log('[PatrolPlans] Fetching sites...');
        const sitesRes = await api.get('/sites?limit=500');
        const sitesData = sitesRes.data.items || sitesRes.data || [];
        console.log('[PatrolPlans] Loaded sites:', sitesData.length);
        setSites(sitesData);
        
        // Fetch clients
        console.log('[PatrolPlans] Fetching clients...');
        const clientsRes = await api.get('/clients');
        const clientsData = clientsRes.data.items || clientsRes.data || [];
        console.log('[PatrolPlans] Loaded clients:', clientsData.length);
        setClients(clientsData);
      } catch (err) {
        console.error('[PatrolPlans] Failed to fetch sites/clients:', err);
        toast.error('Failed to load dropdown data');
      }
    };
    
    fetchData();
  }, [api]);

  // Fetch plans on filter change
  useEffect(() => {
    fetchPlans();
  }, [fetchPlans]);

  // Reset form
  const resetForm = () => {
    setFormData({
      name: '',
      region: '',
      client_id: '',
      description: '',
      schedule: {
        days_of_week: [1, 2, 3, 4, 5],
        start_time: '06:00',
        end_time: '14:00',
        timezone: 'Australia/Sydney'
      },
      patrol_config: {
        expected_visits: 1,
        expected_duration_minutes: 480,
        allow_out_of_order: true,
        require_site_checkout: true
      },
      sites: []
    });
  };

  // Open create dialog
  const handleCreate = () => {
    resetForm();
    setShowCreateDialog(true);
  };

  // Open edit dialog
  const handleEdit = (plan) => {
    setSelectedPlan(plan);
    setFormData({
      name: plan.name || '',
      region: plan.region || '',
      client_id: plan.client_id || '',
      description: plan.description || '',
      schedule: plan.schedule || {
        days_of_week: [1, 2, 3, 4, 5],
        start_time: '06:00',
        end_time: '14:00',
        timezone: 'Australia/Sydney'
      },
      patrol_config: plan.patrol_config || {
        expected_visits: 1,
        expected_duration_minutes: 480,
        allow_out_of_order: true,
        require_site_checkout: true
      },
      sites: plan.sites || []
    });
    setShowEditDialog(true);
  };

  // Open delete confirmation
  const handleDeleteClick = (plan) => {
    setSelectedPlan(plan);
    setShowDeleteDialog(true);
  };

  // Submit create
  const handleCreateSubmit = async () => {
    if (!formData.name.trim()) {
      toast.error('Plan name is required');
      return;
    }
    
    setFormLoading(true);
    try {
      await api.post('/patrol-plans', formData);
      toast.success('Patrol plan created successfully');
      setShowCreateDialog(false);
      fetchPlans();
    } catch (err) {
      console.error('Create error:', err);
      toast.error(err.response?.data?.detail || 'Failed to create patrol plan');
    } finally {
      setFormLoading(false);
    }
  };

  // Submit update
  const handleUpdateSubmit = async () => {
    if (!formData.name.trim()) {
      toast.error('Plan name is required');
      return;
    }
    
    setFormLoading(true);
    try {
      await api.put(`/patrol-plans/${selectedPlan.id}`, formData);
      toast.success('Patrol plan updated successfully');
      setShowEditDialog(false);
      fetchPlans();
    } catch (err) {
      console.error('Update error:', err);
      toast.error(err.response?.data?.detail || 'Failed to update patrol plan');
    } finally {
      setFormLoading(false);
    }
  };

  // Submit delete
  const handleDeleteConfirm = async () => {
    setFormLoading(true);
    try {
      await api.delete(`/patrol-plans/${selectedPlan.id}`);
      toast.success('Patrol plan deleted');
      setShowDeleteDialog(false);
      fetchPlans();
    } catch (err) {
      console.error('Delete error:', err);
      toast.error(err.response?.data?.detail || 'Failed to delete patrol plan');
    } finally {
      setFormLoading(false);
    }
  };

  // Format days for display
  const formatDays = (days) => {
    if (!days || days.length === 0) return 'None';
    if (days.length === 7) return 'Every day';
    const sorted = [...days].sort((a, b) => a - b);
    if (JSON.stringify(sorted) === JSON.stringify([1, 2, 3, 4, 5])) return 'Weekdays';
    if (JSON.stringify(sorted) === JSON.stringify([6, 7])) return 'Weekends';
    return days.map(d => DAYS_OF_WEEK.find(day => day.value === d)?.label).join(', ');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="rx-patrol-plans space-y-6" data-testid="patrol-plans-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="rx-patrol-page-title text-2xl font-bold flex items-center gap-2">
            <Route className="w-6 h-6 text-blue-400" />
            Patrol Plans
          </h1>
          <p className="text-sm text-zinc-500 mt-1">
            Define named patrol runs grouping multiple sites with schedules
          </p>
        </div>
        
        <Button
          onClick={handleCreate}
          className="bg-blue-600 hover:bg-blue-700"
          data-testid="create-patrol-plan-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          New Patrol Plan
        </Button>
      </div>
      
      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
          <Input
            placeholder="Search plans..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>
        
        <Select value={filterRegion} onValueChange={setFilterRegion}>
          <SelectTrigger className="w-36">
            <SelectValue placeholder="Region" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Regions</SelectItem>
            {REGIONS.map(r => (
              <SelectItem key={r} value={r}>{r}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        
        <Select value={filterActive} onValueChange={setFilterActive}>
          <SelectTrigger className="w-32">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="inactive">Inactive</SelectItem>
          </SelectContent>
        </Select>
      </div>
      
      {/* Plans Grid */}
      {plans.length === 0 ? (
        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardContent className="py-12 text-center">
            <Route className="w-12 h-12 mx-auto text-zinc-600 mb-4" />
            <h3 className="text-lg font-medium text-zinc-400">No patrol plans found</h3>
            <p className="text-sm text-zinc-600 mt-1">
              Create your first patrol plan to group sites into named runs
            </p>
            <Button
              onClick={handleCreate}
              className="mt-4 bg-blue-600 hover:bg-blue-700"
            >
              <Plus className="w-4 h-4 mr-2" />
              Create Patrol Plan
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {plans.map(plan => (
            <Card
              key={plan.id}
              className="rx-patrol-card bg-zinc-900/50 border-zinc-800 hover:border-zinc-700 transition-colors"
              data-testid={`patrol-plan-${plan.id}`}
            >
              <CardHeader className="pb-2">
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <CardTitle className="rx-patrol-card-title text-lg text-white truncate">
                      {plan.name}
                    </CardTitle>
                    <div className="flex items-center gap-2 mt-1">
                      {plan.region && (
                        <Badge variant="outline" className="text-xs">
                          {plan.region}
                        </Badge>
                      )}
                      <Badge
                        variant="outline"
                        className={plan.is_active 
                          ? 'border-emerald-500/50 text-emerald-400' 
                          : 'border-zinc-600 text-zinc-500'
                        }
                      >
                        {plan.is_active ? 'Active' : 'Inactive'}
                      </Badge>
                    </div>
                  </div>
                  <div className="flex gap-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-8 w-8 p-0"
                      onClick={() => handleEdit(plan)}
                    >
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-8 w-8 p-0 text-red-400 hover:text-red-300"
                      onClick={() => handleDeleteClick(plan)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              
              <CardContent className="space-y-3">
                {/* Client */}
                {plan.client_name && (
                  <div className="flex items-center gap-2 text-sm text-zinc-400">
                    <Building2 className="w-4 h-4" />
                    <span className="truncate">{plan.client_name}</span>
                  </div>
                )}
                
                {/* Sites count */}
                <div className="flex items-center gap-2 text-sm text-zinc-400">
                  <MapPin className="w-4 h-4" />
                  <span>{plan.site_count || 0} sites</span>
                </div>
                
                {/* Schedule */}
                <div className="flex items-center gap-2 text-sm text-zinc-400">
                  <Calendar className="w-4 h-4" />
                  <span>{formatDays(plan.schedule?.days_of_week)}</span>
                </div>
                
                {/* Time */}
                {plan.schedule?.start_time && (
                  <div className="flex items-center gap-2 text-sm text-zinc-400">
                    <Clock className="w-4 h-4" />
                    <span>{plan.schedule.start_time} - {plan.schedule.end_time}</span>
                  </div>
                )}
                
                {/* Description */}
                {plan.description && (
                  <p className="text-xs text-zinc-500 line-clamp-2">
                    {plan.description}
                  </p>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
      
      {/* Create Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-2xl bg-zinc-900 border-zinc-700">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Route className="w-5 h-5 text-blue-400" />
              Create Patrol Plan
            </DialogTitle>
            <DialogDescription>
              Define a named patrol run with grouped sites and schedule
            </DialogDescription>
          </DialogHeader>
          <PlanForm 
            formData={formData}
            setFormData={setFormData}
            sites={sites}
            clients={clients}
            onSubmit={handleCreateSubmit} 
            submitLabel="Create Plan"
            formLoading={formLoading}
          />
        </DialogContent>
      </Dialog>
      
      {/* Edit Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-2xl bg-zinc-900 border-zinc-700">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Pencil className="w-5 h-5 text-blue-400" />
              Edit Patrol Plan
            </DialogTitle>
            <DialogDescription>
              Update the patrol plan settings and sites
            </DialogDescription>
          </DialogHeader>
          <PlanForm 
            formData={formData}
            setFormData={setFormData}
            sites={sites}
            clients={clients}
            onSubmit={handleUpdateSubmit} 
            submitLabel="Save Changes"
            formLoading={formLoading}
          />
        </DialogContent>
      </Dialog>
      
      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent className="bg-zinc-900 border-zinc-700">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-red-400" />
              Delete Patrol Plan
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to delete "{selectedPlan?.name}"? 
              This action will soft-delete the plan. Existing shifts linked to this plan will not be affected.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteConfirm}
              disabled={formLoading}
            >
              {formLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
